import { api } from './api.js';

/**
 * What people do in the app, sent as named events a few at a time, so the
 * product can be shaped by what is used and where people stop. Names and
 * small values only: never an amount typed, an address or a phrase.
 *
 * A device id stands in for someone who has not made a wallet yet. It lives
 * in localStorage and means nothing anywhere else.
 */
type Props = Record<string, string | number | boolean>;

const KEY = 'xchange.device';
let deviceId: string | null = null;
let platform: 'web' | 'telegram' = 'web';
let queue: { name: string; props?: Props; at: string }[] = [];
let timer: number | null = null;
let sending = false;

function device(): string {
  if (deviceId !== null) return deviceId;
  let id: string | null = null;
  try {
    id = localStorage.getItem(KEY);
  } catch {
    /* no storage */
  }
  if (id === null || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* fine: the id lives for this page */
    }
  }
  deviceId = id;
  return id;
}

export function setTrackingPlatform(kind: 'web' | 'telegram'): void {
  platform = kind;
}

/** Note that something happened. Cheap; batches go out every few seconds. */
export function track(name: string, props?: Props): void {
  queue.push({ name, ...(props === undefined ? {} : { props }), at: new Date().toISOString() });
  if (queue.length >= 20) void flush();
  else timer ??= window.setTimeout(() => void flush(), 4000);
}

/** Send what is queued now: for the last event of a visit, noted as the page is hidden. */
export function sendNow(): void {
  void flush();
}

/** The same, waited for: before a reload that would lose the queue. */
export function sendNowAndWait(): Promise<void> {
  return flush();
}

async function flush(): Promise<void> {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  if (sending || queue.length === 0) return;
  const batch = queue.slice(0, 50);
  queue = queue.slice(50);
  sending = true;
  try {
    await api.events({ deviceId: device(), platform, events: batch });
  } catch {
    /* lost; never worth a retry that could duplicate */
  } finally {
    sending = false;
    if (queue.length > 0) void flush();
  }
}

// Whatever is queued leaves when the page is hidden, which is when most apps lose it.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  // Some in-app browsers close the page without a visibility change first.
  window.addEventListener('pagehide', () => void flush());
}

/** Something that failed on the person's device; the server would never see it otherwise. */
export function trackError(where: string): void {
  track('error', { where });
}
